import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { Link } from '@tanstack/react-router';
import { PublicLayout } from '../components/public-layout.js';
import { SectionBlock } from '../components/section-block.js';

// ─── 의료법 5대 원칙 준수 안내 ────────────────────────────────────────────────
const LAW_PRINCIPLES = [
  {
    n: '제56조',
    title: '과장·허위 광고 금지',
    desc: '의료기관 명칭·실적에 대한 과장·허위 표현을 사용하지 않습니다.',
  },
  {
    n: '제56조',
    title: '비교 광고 금지',
    desc: '타 의료기관과 비교하거나 우열을 표시하는 표현을 사용하지 않습니다.',
  },
  {
    n: '제27조',
    title: '유인·알선 금지',
    desc: '환자 유인·알선에 해당할 수 있는 표현을 일절 포함하지 않습니다.',
  },
  {
    n: '제46조',
    title: '의료기관 광고 준수',
    desc: '의료기관 정보는 사실에 근거하여 기재됩니다.',
  },
  {
    n: '시행규칙',
    title: '사실에 근거한 정보만 제공',
    desc: '기재된 모든 정보는 의료기관의 사전 동의를 받아 게재됩니다.',
  },
];

// ─── 도입 의료기관 데이터 (실제 데이터 연동 전 플레이스홀더) ──────────────────
const INSTITUTIONS: Array<{
  name: string;
  region: string;
  type: string;
  solutions: string[];
}> = [
  {
    name: '<실제 데이터로 교체>',
    region: '서울',
    type: '신경과 의원',
    solutions: ['MMSE-BeauBrain', 'SCST', 'PASCAL-Med'],
  },
  {
    name: '<실제 데이터로 교체>',
    region: '경기',
    type: '내과·신경과 클리닉',
    solutions: ['MMSE-BeauBrain', 'SCST'],
  },
  {
    name: '<실제 데이터로 교체>',
    region: '부산',
    type: '신경과 의원',
    solutions: ['SCST', 'Amylo-Cloud', 'PASCAL-Med'],
  },
];

// stage colors for solution chips
const SOL_COLOR: Record<string, string> = {
  'MMSE-BeauBrain': '#3b82f6',
  SCST: '#22c55e',
  'Amylo-Cloud': '#a78bfa',
  MORPH: '#a78bfa',
  'PASCAL-Med': '#f59e0b',
};

function SolChip({ name }: { name: string }) {
  const c = SOL_COLOR[name] ?? 'var(--ink-3)';
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        color: c,
        background: `${c}1a`,
        border: `1px solid ${c}33`,
        borderRadius: 4,
        padding: '2px 7px',
        marginRight: 4,
      }}
    >
      {name}
    </span>
  );
}

// ─── PAGE ─────────────────────────────────────────────────────────────────────
function Page() {
  return (
    <PublicLayout>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section style={{ padding: '160px 0 80px', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <span className="section-label">도입기관 디렉토리</span>
          <h1 className="section-title" style={{ marginTop: 12, maxWidth: 680 }}>
            도입 의료기관 현황
          </h1>
          <p
            style={{
              color: 'var(--ink-2)',
              fontSize: 17,
              marginTop: 20,
              lineHeight: 1.7,
              maxWidth: 580,
            }}
          >
            BeauBrain 솔루션을 도입한 의료기관 정보를 안내합니다. 모든 정보는 의료기관의 사전 동의를
            받아 게재되며, 의료법 광고 기준을 준수합니다.
          </p>

          {/* 의료법 광고 준수 배너 */}
          <div
            style={{
              marginTop: 32,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 16px',
              background: 'rgba(0,229,255,0.04)',
              border: '1px solid rgba(0,229,255,0.12)',
              borderRadius: 8,
              fontSize: 12,
              color: 'var(--ink-3)',
            }}
          >
            <span style={{ color: 'var(--accent)', fontWeight: 700 }}>i</span>
            본 페이지는 의료법 제56조(의료광고 기준) 및 제27조(유인·알선 금지)를 준수합니다.
          </div>
        </div>
      </section>

      {/* ── 의료법 5대 원칙 ────────────────────────────────────────────────── */}
      <section style={{ padding: '80px 0', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <SectionBlock
            label="Legal Compliance"
            title="의료법 광고 5대 원칙 준수"
            subtitle="본 페이지에 게재되는 의료기관 정보는 아래 5가지 의료법 원칙을 준수하여 구성됩니다."
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 14,
                marginTop: 48,
              }}
            >
              {LAW_PRINCIPLES.map((p) => (
                <div
                  key={p.title}
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid var(--line)',
                    borderRadius: 12,
                    padding: '20px 18px',
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--accent)',
                      letterSpacing: '0.06em',
                      marginBottom: 8,
                    }}
                  >
                    의료법 {p.n}
                  </div>
                  <div
                    style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 8, lineHeight: 1.4 }}
                  >
                    {p.title}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.6 }}>{p.desc}</div>
                </div>
              ))}
            </div>
          </SectionBlock>
        </div>
      </section>

      {/* ── 도입 의료기관 목록 ─────────────────────────────────────────────── */}
      <section style={{ padding: '100px 0', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <SectionBlock
            label="Institutions"
            title="도입 의료기관"
            subtitle="아래 정보는 의료기관의 사전 동의 하에 게재된 사실 정보입니다. 특정 의료기관을 추천하거나 우열을 표시하지 않습니다."
          >
            <div
              style={{
                marginTop: 48,
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid var(--line)',
                borderRadius: 16,
                overflow: 'hidden',
              }}
            >
              {/* header */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1.5fr 2fr',
                  padding: '12px 28px',
                  background: 'rgba(255,255,255,0.03)',
                  borderBottom: '1px solid var(--line)',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-3)',
                }}
              >
                <span>의료기관명</span>
                <span>지역</span>
                <span>진료과·형태</span>
                <span>도입 솔루션</span>
              </div>

              {INSTITUTIONS.map((inst, i) => (
                <div
                  key={`${inst.name}-${i}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1.5fr 2fr',
                    padding: '20px 28px',
                    borderBottom: i < INSTITUTIONS.length - 1 ? '1px solid var(--line)' : 'none',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}
                  >
                    {inst.name}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{inst.region}</div>
                  <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{inst.type}</div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {inst.solutions.map((s) => (
                      <SolChip key={s} name={s} />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* placeholder notice */}
            <div
              style={{
                marginTop: 16,
                padding: '14px 18px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid var(--line)',
                borderRadius: 10,
                fontSize: 12,
                color: 'var(--ink-3)',
                lineHeight: 1.6,
              }}
            >
              ※ 위 의료기관 정보는 실제 데이터로 교체 예정입니다. 게재를 원하는 의료기관은 도입 담당자에게
              문의해 주세요. 의료기관의 사전 동의 없이는 게재하지 않습니다.
            </div>
          </SectionBlock>
        </div>
      </section>

      {/* ── 도입 절차 ─────────────────────────────────────────────────────────── */}
      <section style={{ padding: '100px 0', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <SectionBlock label="How to Join" title="도입 절차">
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginTop: 56 }}
            >
              {[
                { n: 1, step: '도입 문의', desc: '이메일 또는 전화로 BeauBrain 담당자에게 도입 의사를 전달합니다.' },
                { n: 2, step: '솔루션 데모', desc: '원하는 솔루션의 데모를 통해 의원 운영 환경을 확인합니다.' },
                { n: 3, step: '계약 및 설치', desc: '계약 후 태블릿 설정·대시보드 교육을 담당자가 지원합니다.' },
                { n: 4, step: '디렉토리 등록 (선택)', desc: '동의 시 도입기관 디렉토리에 의료기관 정보가 게재됩니다.' },
              ].map((s) => (
                <div
                  key={s.n}
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid var(--line)',
                    borderRadius: 14,
                    padding: 24,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: 'rgba(0,229,255,0.08)',
                      border: '1px solid rgba(0,229,255,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: "'Inter Tight', sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      color: 'var(--accent)',
                      marginBottom: 16,
                    }}
                  >
                    {s.n}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>
                    {s.step}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{s.desc}</div>
                </div>
              ))}
            </div>
          </SectionBlock>
        </div>
      </section>

      {/* ── 의료법 전문 면책 고지 ─────────────────────────────────────────────── */}
      <section style={{ padding: '60px 0', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <div
            className="disclaimer-card-apple"
            style={{ maxWidth: 760, margin: '0 auto' }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--ink-3)',
                marginBottom: 16,
              }}
            >
              — 의료법 광고 준수 고지
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.8 }}>
              <p style={{ marginBottom: 10 }}>
                본 페이지에 게재된 의료기관 정보는 의료법 제56조(의료광고 기준), 제27조(유인·알선 금지),
                제46조(의료기관 광고) 등 관련 법령을 준수하여 작성되었습니다.
              </p>
              <p style={{ marginBottom: 10 }}>
                게재된 정보는 의료기관의 사전 동의를 받아 사실에 근거하여 기재되었으며,
                특정 의료기관을 다른 의료기관과 비교하거나 우열을 표시하지 않습니다.
                환자 유인·알선에 해당하는 표현은 포함되지 않습니다.
              </p>
              <p style={{ marginBottom: 0 }}>
                디렉토리 게재 정보의 정확성에 대한 최종 확인은 의료기관에 직접 문의하시기 바랍니다.
                BeauBrain은 의료기관의 진료 수준·결과를 보증하지 않습니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────────── */}
      <section
        style={{
          padding: '100px 0',
          textAlign: 'center',
          background: 'rgba(255,255,255,0.01)',
        }}
      >
        <div className="container">
          <h2
            style={{
              fontSize: 38,
              fontWeight: 700,
              color: 'var(--ink)',
              marginBottom: 16,
              letterSpacing: '-0.03em',
            }}
          >
            도입을 검토 중이신가요?
          </h2>
          <p style={{ color: 'var(--ink-3)', marginBottom: 36, fontSize: 16 }}>
            먼저 솔루션 데모를 통해 의원 운영 환경에 맞는 구성을 확인해 보세요.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <a href="mailto:contact@beaubrain.bio" className="btn-primary btn-lg">
              도입 문의하기 →
            </a>
            <Link to="/credit" className="btn-outline btn-lg">
              통합 크레딧 알아보기
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

export const Route = createFileRoute('/directory')({
  component: Page,
});
